import { printHtml } from "@/components/inventory/shelfPrint";

const esc = (v) => String(v ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const fmtN = (n) => Number(n || 0).toLocaleString();
const fmtDate = (s) => {
  if (!s) return "—";
  const d = new Date(String(s).length <= 10 ? `${s}T00:00:00` : s);
  return isNaN(d) ? s : d.toLocaleDateString();
};

export const printPurchaseOrder = (po, company = {}, supplier = null) => {
  const cur = (v) => "$" + Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const hasPrices = (po.lines || []).some((l) => Number(l.unit_price) > 0);
  const rows = (po.lines || []).map((l) => `
    <tr>
      <td><b>${esc(l.custom ? (l.custom_name || l.name || "Custom item") : l.component_name)}</b>${l.sku ? `<div style="color:#64748b">SKU ${esc(l.sku)}</div>` : ""}</td>
      <td>${esc(l.custom ? (l.description || "") : [l.type_name, l.design_name].filter(Boolean).join(" · ")) || "—"}</td>
      <td class="r">${fmtN(l.quantity)}</td>
      <td>${esc(l.unit || "units")}</td>
      ${hasPrices ? `<td class="r">${Number(l.unit_price) > 0 ? esc(cur(l.unit_price)) : "—"}</td><td class="r">${Number(l.line_total) > 0 ? esc(cur(l.line_total)) : "—"}</td>` : ""}
    </tr>`).join("");
  const totalUnits = (po.lines || []).reduce((a, l) => a + (Number(l.quantity) || 0), 0);
  const supLines = [supplier?.company_name || po.supplier_name, supplier?.address, supplier?.website].filter(Boolean);
  const shipTo = [company.company_name, company.address_line1, company.address_line2].filter(Boolean);

  const html = `<html><head><title>Purchase Order ${esc(po.po_number)}</title><style>
    * { box-sizing: border-box; }
    body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #0f172a; margin: 32px; font-size: 12px; }
    .top { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 28px; }
    .co { font-size: 12px; line-height: 1.5; color: #334155; }
    .co .name { font-size: 17px; font-weight: 700; color: #0f172a; letter-spacing: -0.02em; }
    .title-block { text-align: right; }
    .title { font-size: 24px; font-weight: 700; letter-spacing: -0.02em; margin-bottom: 10px; }
    .meta { border-collapse: collapse; margin-left: auto; }
    .meta th { font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em; color: #64748b; border: 1px solid #cbd5e1; padding: 3px 10px; background: #f8fafc; }
    .meta td { border: 1px solid #cbd5e1; padding: 4px 10px; text-align: center; font-weight: 600; }
    .parties { display: flex; gap: 24px; margin-bottom: 20px; }
    .party { flex: 1; border: 1px solid #cbd5e1; }
    .party .hdr { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #475569; background: #f1f5f9; padding: 4px 8px; border-bottom: 1px solid #cbd5e1; font-weight: 700; }
    .party .val { padding: 8px; min-height: 44px; line-height: 1.5; }
    .party .val .first { font-weight: 600; }
    table.items { width: 100%; border-collapse: collapse; }
    table.items th { font-size: 9px; text-transform: uppercase; letter-spacing: 0.06em; color: #475569; background: #f1f5f9; border: 1px solid #cbd5e1; padding: 5px 8px; text-align: left; }
    table.items td { border: 1px solid #cbd5e1; padding: 8px; vertical-align: top; }
    .r { text-align: right !important; font-variant-numeric: tabular-nums; }
    .totalrow { display: flex; justify-content: flex-end; margin-top: 14px; }
    .total { display: flex; gap: 28px; border-top: 2px solid #0f172a; padding-top: 8px; font-size: 14px; font-weight: 700; }
    .notes { margin-top: 18px; font-size: 11px; color: #475569; }
    .notes .hdr { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #64748b; font-weight: 700; margin-bottom: 3px; }
    .foot { margin-top: 36px; font-size: 10px; color: #94a3b8; }
    @media print { body { margin: 12mm; } }
  </style></head><body>
    <div class="top">
      <div class="co">
        <div class="name">${esc(company.company_name || "")}</div>
        <div>${esc(company.address_line1 || "")}</div>
        <div>${esc(company.address_line2 || "")}</div>
      </div>
      <div class="title-block">
        <div class="title">Purchase Order</div>
        <table class="meta">
          <tr><th>PO Number</th><th>Order Date</th><th>Required Delivery</th><th>Expected Delivery</th></tr>
          <tr><td>${esc(po.po_number)}</td><td>${esc(fmtDate(po.order_date))}</td><td>${esc(fmtDate(po.rdd))}</td><td>${esc(fmtDate(po.edd || po.eta))}</td></tr>
        </table>
        ${po.payment_terms || po.freight_terms || po.ship_method ? `<div style="margin-top:6px;font-size:10px;color:#475569;text-align:right">
          ${po.payment_terms ? `Payment: <b>${esc(po.payment_terms)}</b>` : ""}
          ${po.freight_terms ? ` · Freight: <b>${esc(po.freight_terms)}</b>` : ""}
          ${po.ship_method ? ` · <b>${po.ship_method === "pickup" ? "We pick up" : "Supplier delivers"}</b>` : ""}
        </div>` : ""}
      </div>
    </div>
    <div class="parties">
      <div class="party"><div class="hdr">Supplier</div><div class="val">${supLines.map((l, i) => `<div class="${i === 0 ? "first" : ""}">${esc(l)}</div>`).join("")}</div></div>
      <div class="party"><div class="hdr">Ship To</div><div class="val">${(po.destination ? po.destination.split("\n") : shipTo).map((l, i) => `<div class="${i === 0 ? "first" : ""}">${esc(l)}</div>`).join("")}</div></div>
    </div>
    <table class="items">
      <thead><tr>
        <th style="width:${hasPrices ? "26%" : "34%"}">Item</th><th style="width:${hasPrices ? "28%" : "38%"}">Variant / Description</th>
        <th class="r" style="width:12%">Quantity</th><th style="width:10%">Unit</th>
        ${hasPrices ? `<th class="r" style="width:12%">Unit Price</th><th class="r" style="width:12%">Total</th>` : ""}
      </tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="totalrow"><div class="total">
      <span>Total Units</span><span>${fmtN(totalUnits)}</span>
      ${hasPrices ? `<span>Subtotal</span><span>${esc(cur(po.subtotal))}</span>` : ""}
      ${Number(po.freight_cost) > 0 ? `<span>Freight</span><span>${esc(cur(po.freight_cost))}</span>` : ""}
      ${hasPrices ? `<span>Total ${esc(po.currency && po.currency !== "USD" ? `(${po.currency})` : "")}</span><span>${esc(cur(po.total))}</span>` : ""}
    </div></div>
    ${po.supplier_instructions ? `<div class="notes"><div class="hdr">Instructions to Supplier</div>${esc(po.supplier_instructions)}</div>` : ""}
    ${po.notes ? `<div class="notes"><div class="hdr">Notes</div>${esc(po.notes)}</div>` : ""}
    <div class="foot">Generated ${new Date().toLocaleString()}</div>
  </body></html>`;
  printHtml(html);
};
